import { useState } from 'react';
import { useTrackedAddress } from '../hooks/useTrackedAddress';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { usePushConfig } from '../hooks/usePushConfig';
import { Button } from '../components/Button';
import { InstallAppButton } from '../components/InstallAppButton';
import { PushOptIn } from '../components/PushOptIn';
import { DEFAULT_ADDRESS, DEFAULT_LABEL } from '../lib/storage';
import { PUSH_TOPICS } from '../lib/pushTopics';
import styles from './SettingsPage.module.css';

export function SettingsPage() {
  const { tracked, updateAddress } = useTrackedAddress();
  const [addressInput, setAddressInput] = useState(tracked.address);
  const [labelInput, setLabelInput] = useState(tracked.label);
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const result = updateAddress(addressInput, labelInput);
    if (result.ok) {
      setFeedback({ type: 'success', message: 'Tracked address updated.' });
    } else {
      setFeedback({ type: 'error', message: result.reason ?? 'Invalid address.' });
    }
  };

  const resetToDefault = () => {
    updateAddress(DEFAULT_ADDRESS, DEFAULT_LABEL);
    setAddressInput(DEFAULT_ADDRESS);
    setLabelInput(DEFAULT_LABEL);
    setFeedback({ type: 'success', message: 'Back to the default address.' });
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.heading}>Settings</h1>
      <p className={styles.subheading}>
        Switch to another valid Bitcoin mainnet address. Addresses are validated locally
        before being saved.
      </p>

      <form className={styles.form} onSubmit={handleSubmit}>
        <label className={styles.field}>
          <span>Bitcoin address</span>
          <input
            type="text"
            className={styles.addressInput}
            value={addressInput}
            onChange={(e) => setAddressInput(e.target.value)}
            spellCheck={false}
            autoCapitalize="none"
            aria-describedby={feedback?.type === 'error' ? 'address-feedback' : undefined}
            aria-invalid={feedback?.type === 'error'}
          />
        </label>

        <label className={styles.field}>
          <span>Label</span>
          <input
            type="text"
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            maxLength={64}
          />
        </label>

        <div className={styles.actions}>
          <Button type="submit" variant="primary">
            Save
          </Button>
          {tracked.address !== DEFAULT_ADDRESS && (
            <Button type="button" onClick={resetToDefault}>
              Reset to default
            </Button>
          )}
        </div>

        {feedback && (
          <p
            id="address-feedback"
            role="status"
            className={feedback.type === 'error' ? styles.errorText : styles.successText}
          >
            {feedback.message}
          </p>
        )}
      </form>

      <div className={styles.currentBlock}>
        <h2 className={styles.currentHeading}>Currently tracking</h2>
        <p className={styles.currentLabel}>{tracked.label}</p>
        <p className={styles.currentAddress}>{tracked.address}</p>
      </div>

      <NotificationSettings />
      <InstallSettings />
    </div>
  );
}

/** Every notification topic in one place, each also offered on its own page. */
function NotificationSettings() {
  const { data } = usePushConfig();
  if (!data?.enabled || !data.publicKey) return null;
  const supported =
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window;
  const blocked =
    typeof Notification !== 'undefined' && Notification.permission === 'denied';
  return (
    <section className={styles.currentBlock} aria-labelledby="notify-heading">
      <h2 id="notify-heading" className={styles.currentHeading}>
        Notifications
      </h2>
      {!supported ? (
        <p className={styles.subheading}>
          This browser can&apos;t show notifications from WhaleTail. On iPhone, add
          WhaleTail to your home screen first and open it from there.
        </p>
      ) : blocked ? (
        <p className={styles.subheading}>
          Notifications are blocked in your browser settings. Allow them for this site to
          turn any of these on.
        </p>
      ) : (
        <ul className={styles.notifyList}>
          {PUSH_TOPICS.map((t) => (
            <li key={t.topic}>
              <PushOptIn topic={t.topic} offLabel={t.offLabel} onLabel={t.onLabel} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Only shown where installing is possible or already done. */
function InstallSettings() {
  const { state } = useInstallPrompt();
  if (state === 'unavailable') return null;
  return (
    <section className={styles.currentBlock} aria-labelledby="install-heading">
      <h2 id="install-heading" className={styles.currentHeading}>
        Install app
      </h2>
      {state === 'installed' ? (
        <p className={styles.subheading}>WhaleTail is installed on this device.</p>
      ) : (
        <>
          <p className={styles.subheading}>
            Add WhaleTail to your home screen for a full-screen, app-like view — and on
            iPhone, this is required before you can turn on signal notifications.
          </p>
          <InstallAppButton />
        </>
      )}
    </section>
  );
}
